'use client';

import { Button, ButtonProps } from '@/components/common/Button/Button';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ArrowGray } from '@/components/common/Icons/ArrowGray';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import { CloseIcon } from '@/components/common/Icons/CloseIcon';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import styles from './Modal.module.css';
import { useRouter } from 'next/navigation';

export interface ModalProps {
	redirectOnClose?: boolean;
	openModal: boolean;
	// setOpenModal: Dispatch<SetStateAction<boolean>>;
	setOpenModal: (isOpen: boolean) => void;
	setOpenPreviousModal?: (isOpen: boolean) => void;
	openButtonText?: React.ReactNode;
	children?: React.ReactNode;
	disabled?: boolean; // For the Button component
	// Applies to the opening button in the event an api call needs to be made.
	apiCall?: () => Promise<unknown>;
	buttonStyle?: 'text' | 'primary' | 'secondary' | 'tertiary' | undefined;
	closeButtonText?: React.ReactNode;
	closeBtnProps?: ButtonProps;
	closeButtonType?: 'button-web' | 'button-mobile';
	closeBtnApiCall?: () => Promise<unknown>;
	footerText?: React.ReactNode;
	fromCheckout?: boolean;
	className?: string;
	closeIconFill?: string;
	closeOnBackdropClick?: boolean;
	closeIconStyles?: string;
	closeButtonStyles?: string;
	closeOnBackdropClickNeeded?: boolean;
	closeButtonNeeded?: boolean;
	preventEscClose?: boolean;
	customDialogStyle?: string;
	customCloseModalAction?: boolean;
	closeModalAction?: () => void;
}

export function Modal({
	redirectOnClose = false,
	openModal,
	setOpenModal,
	setOpenPreviousModal,
	children,
	disabled,
	openButtonText,
	apiCall,
	buttonStyle,
	closeButtonText,
	closeBtnProps,
	closeButtonType,
	closeBtnApiCall,
	footerText,
	fromCheckout = false,
	className,
	closeIconFill,
	closeOnBackdropClick = false,
	closeIconStyles,
	closeButtonStyles,
	closeOnBackdropClickNeeded = false,
	closeButtonNeeded = true,
	preventEscClose = false,
	customDialogStyle,
	customCloseModalAction = false,
	closeModalAction = () => {},
}: ModalProps) {
	const [isLoading, setIsLoading] = useState(false);
	const modalRef = useRef<HTMLDialogElement>(null);
	const { isFromCampaign } = useContext(CartContext);
	const router = useRouter();

	useEffect(() => {
		const dialog = modalRef.current;

		const handleBackdropClick = (e: MouseEvent) => {
			if (closeOnBackdropClick && dialog && e.target === dialog) {
				setOpenModal(false);
			}
		};

		if (closeOnBackdropClickNeeded && dialog) {
			dialog.addEventListener('click', handleBackdropClick);
			return () => dialog.removeEventListener('click', handleBackdropClick);
		}
		return;
	}, [closeOnBackdropClick, closeOnBackdropClickNeeded, setOpenModal]);

	useEffect(() => {
		if (openModal) {
			if (!modalRef.current?.open) {
				modalRef.current?.showModal();
			}
		} else {
			modalRef.current?.close();
		}
	}, [openModal]);

	const onCloseModal = useCallback(() => {
		setOpenModal(false);
		if (redirectOnClose && !isFromCampaign) {
			router.push('/');
		}
		if (customCloseModalAction) {
			closeModalAction();
		}
	}, [
		isFromCampaign,
		redirectOnClose,
		router,
		setOpenModal,
		customCloseModalAction,
		closeModalAction,
	]);

	useEffect(() => {
		if (!preventEscClose) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				console.log('ESC prevented globally');
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [preventEscClose]);

	return (
		<>
			{openButtonText && (
				<Button
					data-type-style="button-web"
					variant={buttonStyle ? buttonStyle : 'primary'}
					disabled={disabled}
					onClick={() => {
						if (apiCall !== undefined) {
							apiCall?.().then((result) => {
								console.log(result);
								setOpenModal(true);
							});
						} else {
							setOpenModal(true);
						}
					}}
				>
					{openButtonText}
				</Button>
			)}

			<dialog
				ref={modalRef}
				className={`${styles.modal} ${className || ''} ${customDialogStyle ?? ''}`}
				data-testid="dialog-modal"
				onCancel={() => {
					if (!preventEscClose) {
						setOpenModal(false);
					}
				}}
			>
				<div>
					{setOpenPreviousModal && (
						<button
							aria-label="previous"
							className={styles.previousIcon}
							onClick={() => {
								setOpenPreviousModal(true);
								if (!fromCheckout) {
									setOpenModal(false);
								}
							}}
						>
							<ArrowGray height={24} width={24} />
						</button>
					)}

					{closeButtonNeeded && (
						<button
							aria-label="close"
							className={cn(styles.closeIcon, closeButtonStyles)}
							onClick={onCloseModal}
							data-testid="closeIcon"
						>
							{redirectOnClose && !isFromCampaign ? (
								<Link className={`${styles.ctaGhost} font-bold`} target="_parent" href="/">
									<CloseIcon height={24} width={24} />
								</Link>
							) : (
								<CloseIcon
									height={24}
									width={24}
									fill={closeIconFill}
									className={cn(closeIconStyles)}
								/>
							)}
						</button>
					)}

					{children}

					{closeButtonText && (
						<footer className="sticky bottom-5 text-center">
							<Button
								style={{ width: '20rem', maxWidth: '100%' }}
								data-type-style={closeButtonType ? closeButtonType : 'button-web'}
								variant="text"
								isLoading={isLoading}
								disabled={isLoading}
								{...closeBtnProps}
								onClick={() => {
									setIsLoading(true);
									if (closeBtnApiCall) {
										closeBtnApiCall().then((result) => {
											setIsLoading(false);
											if (result) {
												setOpenModal(false);
											}
										});
									} else {
										setOpenModal(false);
										setIsLoading(false);
									}
								}}
							>
								{closeButtonText}
							</Button>
						</footer>
					)}
					{footerText}
				</div>
			</dialog>
		</>
	);
}
